import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import i18n from 'i18next';

import TosppBasicBox from './components/tosppBasicBox';
import {
  useLoginLegalDocumentUrlMutation,
  useLoginLegalDocumentAcceptMutation,
  useSsoLegalDocumentAcceptMutation,
} from '@/services/auth.api';
import { dispatch } from '@/store';
import { addToast, ToastType } from '@/store/system';
import { LoginResponse, LegalPending } from '@/services/api.types';
import { advanceLoginStep } from '@/pages/Auth/login-step';
import {
  REDIRECT_SEARCH_PARAM_KEY,
  TEMPORARY,
  LDOC_PENDING,
  LDOC_FLOW,
} from '@/constants/system';
import { appVersion } from '@/utils/version';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';

// legal type slug → legacy numeric type (tos=1, pp=2); tos is always shown first.
const TYPE_NUM: Record<string, number> = { tos: 1, pp: 2 };

// The pending tos/pp list stashed by advanceLoginStep (login-step.ts). May hold
// only tos, only pp, or both; forced tos-before-pp, unknown types dropped.
function readPending(): LegalPending[] {
  try {
    const raw = localStorage.getItem(LDOC_PENDING);
    const list: LegalPending[] = raw ? JSON.parse(raw) : [];
    return list
      .filter(d => TYPE_NUM[d.type])
      .sort((a, b) => TYPE_NUM[a.type] - TYPE_NUM[b.type]);
  } catch {
    return [];
  }
}

// TOS/PP consent gate (LDOC) of the backend-enforced login transaction (ADR-03,
// batch 003). Reached when the step response is pending{next=STEP_LDOC}. Drives
// the pending docs generically (1 or 2, tos first): each doc's HTML is fetched via
// a freshly-signed short-lived URL and shown scroll-to-accept; the last doc's
// accept records all pending versions and advances the transaction off the
// response body. Legacy V1/V2 policy endpoints, initialAccess/activate and the
// temp_reset/pwd_reset chaining are gone (order is now BE-driven via `next`).
const TosAndPP = () => {
  const navigate = useNavigate();
  const { lt } = useLevvelTranslation();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const account = (state as any)?.account;
  const redirect = searchParams.get(REDIRECT_SEARCH_PARAM_KEY);

  const steps = useMemo(() => readPending(), []);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [content, setContent] = useState('');

  const [getUrl] = useLoginLegalDocumentUrlMutation();
  const [acceptPasswordFlow, { isLoading: isPasswordAccepting }] =
    useLoginLegalDocumentAcceptMutation();
  const [acceptSsoFlow, { isLoading: isSsoAccepting }] =
    useSsoLegalDocumentAcceptMutation();

  // Which accept endpoint this consent step belongs to (batch 004, ADR-9). The tmp
  // ticket's audience is the real discriminator — aud=ldoc for password, aud=ldoc.sso
  // for SSO — and it is not readable from the payload, so login-step.ts recorded the
  // flow when it stashed the challenge. Picking wrong yields a 401, never a token the
  // caller should not hold: the audience check is fail-closed server-side.
  const isSsoFlow = localStorage.getItem(LDOC_FLOW) === 'sso';
  const accept = isSsoFlow ? acceptSsoFlow : acceptPasswordFlow;
  const isLoading = isSsoFlow ? isSsoAccepting : isPasswordAccepting;

  const tmpToken = localStorage.getItem(TEMPORARY) ?? '';
  const current = steps[currentIdx];
  const isLast = currentIdx === steps.length - 1;

  const toastFail = (msgKey: string) => {
    dispatch(
      addToast({
        type: ToastType.Danger,
        msg: lt(msgKey),
        title: (
          <div className="letter-space3">
            {lt('cdb_message_status_failed_text')}
          </div>
        ) as any,
      })
    );
  };

  const backToLogin = () => {
    toastFail('auth_mfa_send_code_401_error_text');
    navigate('/login', { state: { account } });
  };

  // No pending docs at this gate = malformed state — fail loud, back to login.
  useEffect(() => {
    if (steps.length === 0) {
      navigate('/login', { state: { account } });
    }
  }, []);

  // Fetch the current doc's HTML via a freshly-signed short-lived URL; refetch on
  // step or language change. On failure never feed the error body to the box.
  useEffect(() => {
    if (!current) return;
    let alive = true;
    setContent('');
    const lang = i18n.language === 'en' ? 'English' : 'Danish';
    getUrl({ versionId: current.versionId, lang, tmpToken })
      .unwrap()
      .then(({ url }) => fetch(url))
      .then(r => {
        if (!r.ok) throw new Error(`content ${r.status}`);
        return r.text();
      })
      .then(html => {
        if (alive) setContent(html);
      })
      .catch(err => {
        if (err?.status === 401) backToLogin();
        else toastFail('common_error_try_again_later');
      });
    return () => {
      alive = false;
    };
  }, [currentIdx, i18n.language]);

  const doAccept = () => {
    const tos = steps.find(d => d.type === 'tos');
    const pp = steps.find(d => d.type === 'pp');
    accept({
      tosVersionId: tos?.versionId,
      ppVersionId: pp?.versionId,
      platformVersion: 'web-' + appVersion,
      tmpToken,
    })
      .unwrap()
      .then((resp: LoginResponse) => {
        // completed → land; pending → next gate (e.g. STEP_PWD) via step-router.
        // Carry the flow forward: if the BE ever chains another gate off this one,
        // defaulting back to 'password' would overwrite an 'sso' marker and send the
        // next accept to the endpoint that 401s it.
        advanceLoginStep({
          resp,
          navigate,
          dispatch,
          redirect,
          account,
          flow: isSsoFlow ? 'sso' : 'password',
        });
      })
      .catch(err => {
        if (err?.status === 401) backToLogin();
        // 409 = version no longer current; other = generic. Both stay for retry.
        else toastFail('common_error_try_again_later');
      });
  };

  // TosppBasicBox button: non-last step → advance to next doc; last → accept.
  const handleSubmit = () => {
    if (isLast) doAccept();
    else setCurrentIdx(idx => idx + 1);
  };

  if (!current) return null;

  const info = {
    type: TYPE_NUM[current.type],
    content,
    hasUpdate: !!current.updated,
    publishedAt: current.publishedAt,
  };

  return (
    <TosppBasicBox
      info={info}
      isLast={isLast}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
    />
  );
};

export default TosAndPP;
